import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import HuttonsWhiteLogo from '../../img/Huttons_white.png';
import { savePdfDocument } from './savePdfDocument';

// ── Color palette (matches affordabilityPdfExport.ts) ───────────────────
const ORANGE_RGB: [number, number, number] = [255, 152, 0];
const DARK_BG_RGB: [number, number, number] = [15, 23, 42];
const FOOTER_BG_RGB: [number, number, number] = [20, 30, 48];
const INFO_BOX_BG_RGB: [number, number, number] = [30, 41, 59];
const SUMMARY_BAR_NAVY_RGB: [number, number, number] = [15, 27, 58];
const SECTION_HEADER_BLUE_RGB: [number, number, number] = [46, 84, 148];
const ZEBRA_GRAY_RGB: [number, number, number] = [245, 247, 249];
const POSITIVE_GREEN_RGB: [number, number, number] = [200, 240, 200];
const NEGATIVE_RED_RGB: [number, number, number] = [255, 220, 220];

// ── Types ────────────────────────────────────────────────────────────────

export interface AgentInfo {
  Name?: string;
  CEANumber?: string;
  Mobile?: string;
  Email?: string;
  Photo?: string;
}

export interface TimelineRow {
  date: string;
  age: number;
  monthlySalary: number;
  cashSavingsMonthly: number;
  cpfOAMonthly: number;
  cpfOAAccumulated: number;
  cashSavingsAccumulated: number;
  cpfRefundFromSales: number;
  cashProceedsFromSale: number;
  totalCapital: number;
  targetPurchasePrice: number;
  maxPurchasePrice: number;
  maxLoan: number;
  loanTenure: number;
  loanToValueRatio: number;
  downpayment5Cash: number;
  remainingDownpayment: number;
  stampDuty: number;
  absd: number;
  totalOutlay: number;
  difference: number;
  affordableToday: number;
}

export interface ProfileRowData {
  age: number;
  assessableIncome: number;
  cashSavingsMonthly: number;
  cpfOAMonthly: number;
}

export interface PurchasePlannerPdfSection {
  propertyType: 'PRIVATE' | 'EC' | 'HDB';
  propertyTypeDisplay: string;
  loanType: 'hdb' | 'bank';
  timelineData: TimelineRow[];
  profileRowData: ProfileRowData[][];  // [profileIndex][rowIndex]
  showRemainingDownpayment: boolean;
  showAffordableToday: boolean;
}

export interface PurchasePlannerPdfData {
  clientName?: string;
  agentInfo?: AgentInfo;
  profileNames: string[];
  sections: PurchasePlannerPdfSection[];
}

// ── Helpers ──────────────────────────────────────────────────────────────

const formatCurrency = (value: number): string =>
  `$${value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

const loadImageEl = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    const t = setTimeout(() => reject(new Error('timeout')), 10000);
    img.onload = () => { clearTimeout(t); resolve(img); };
    img.onerror = () => { clearTimeout(t); reject(new Error('failed')); };
    img.src = url;
  });

const loadUserPhoto = async (doc: jsPDF, agent: AgentInfo, photoX: number, photoY: number, radius: number): Promise<boolean> => {
  if (!agent.Photo) return false;
  try {
    const photoPath = agent.Photo.startsWith('/') ? agent.Photo.substring(1) : agent.Photo;
    const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    return await new Promise<boolean>((resolve) => {
      const timeout = setTimeout(() => resolve(false), 5000);
      img.onload = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (ctx) {
            const size = radius * 2;
            const px = Math.max(256, Math.round(size * 12));
            canvas.width = px;
            canvas.height = px;
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            const r = px / 2;
            const maxInCircle = r * Math.SQRT2;
            const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
            const sw = Math.round(img.width * scale);
            const sh = Math.round(img.height * scale);
            const sx = Math.round((px - sw) / 2);
            const sy = Math.round((px - sh) / 2);
            ctx.save();
            ctx.beginPath();
            ctx.arc(r, r, r, 0, Math.PI * 2);
            ctx.clip();
            ctx.drawImage(img, sx, sy, sw, sh);
            ctx.restore();
            const imgData = canvas.toDataURL('image/png');
            doc.addImage(imgData, 'PNG', photoX - radius, photoY - radius, size, size);
          }
          doc.setDrawColor(...ORANGE_RGB);
          doc.setLineWidth(2);
          doc.circle(photoX, photoY, radius, 'D');
          resolve(true);
        } catch { resolve(false); }
      };
      img.onerror = () => { clearTimeout(timeout); resolve(false); };
      img.src = photoUrl;
    });
  } catch { return false; }
};

// ── Footer ──────────────────────────────────────────────────────────────
const addFooter = (doc: jsPDF, pageNum: number, logoDataUrl?: string, logoAspect?: number, agentInfo?: AgentInfo) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const footerY = pageHeight - 15;
  doc.setFillColor(...FOOTER_BG_RGB);
  doc.rect(0, footerY, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  if (logoDataUrl && logoAspect) {
    const logoH = 7;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', 10, footerY + (15 - logoH) / 2, logoW, logoH);
  } else {
    doc.text('Huttons', 10, footerY + 8);
  }
  doc.text(`${pageNum}`, pageWidth / 2, footerY + 8, { align: 'center' });
  if (agentInfo?.Name) {
    doc.setTextColor(...ORANGE_RGB);
    doc.setFont('helvetica', 'bold');
    doc.text(agentInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    if (agentInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      doc.text(`CEA: ${agentInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
    }
  } else {
    doc.setTextColor(200, 200, 200);
    doc.text('Purchase Planner Report', pageWidth - 10, footerY + 8, { align: 'right' });
  }
};

// ── Page 1: Cover (portrait) ────────────────────────────────────────────
const renderCoverPage = async (doc: jsPDF, data: PurchasePlannerPdfData, logoDataUrl?: string, logoAspect?: number) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const agent = data.agentInfo;

  doc.setFillColor(...DARK_BG_RGB);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');

  doc.setFillColor(...ORANGE_RGB);
  doc.rect(0, 0, pageWidth, 8, 'F');

  let yPos = 28;

  if (logoDataUrl && logoAspect) {
    const logoH = 12;
    const logoW = logoH * logoAspect;
    doc.addImage(logoDataUrl, 'PNG', (pageWidth - logoW) / 2, yPos - logoH + 2, logoW, logoH);
  } else {
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  }
  yPos += 10;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(200, 200, 200);
  doc.text("SINGAPORE'S LARGEST PRIVATE REAL ESTATE AGENCY", pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 45, yPos, 90, 1.2, 'F');

  yPos += 22;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('PURCHASE PLANNER REPORT', pageWidth / 2, yPos, { align: 'center' });
  yPos += 10;

  // Property types included
  const typeLabels = data.sections.map((s) => s.propertyTypeDisplay);
  doc.setTextColor(...ORANGE_RGB);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(typeLabels.join('  |  '), pageWidth / 2, yPos, { align: 'center' });
  yPos += 12;

  // Agent info card
  if (agent) {
    const infoBoxW = 120;
    const infoBoxH = 50;
    const infoBoxX = (pageWidth - infoBoxW) / 2;
    const infoBoxY = yPos;

    doc.setFillColor(...INFO_BOX_BG_RGB);
    doc.setDrawColor(...ORANGE_RGB);
    doc.setLineWidth(2);
    doc.roundedRect(infoBoxX, infoBoxY, infoBoxW, infoBoxH, 3, 3, 'FD');

    const photoRadius = 18;
    const photoPadding = 10;
    const photoX = infoBoxX + photoPadding + photoRadius;
    const photoY = infoBoxY + infoBoxH / 2;

    const photoLoaded = await loadUserPhoto(doc, agent, photoX, photoY, photoRadius);
    if (!photoLoaded) {
      doc.setFillColor(...ORANGE_RGB);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = agent.Name
        ? agent.Name.split(' ').map((n) => n[0]).join('').substring(0, 2).toUpperCase()
        : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }

    const textX = infoBoxX + photoPadding + photoRadius * 2 + 15;
    let detailY = infoBoxY + 12;

    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    if (agent.Name) { doc.text(agent.Name, textX, detailY); detailY += 7; }

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    if (agent.CEANumber) {
      const label = 'CEA Registration: ';
      doc.setTextColor(255, 255, 255);
      doc.text(label, textX, detailY);
      doc.setTextColor(...ORANGE_RGB);
      doc.setFont('helvetica', 'bold');
      doc.text(agent.CEANumber, textX + doc.getTextWidth(label), detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    if (agent.Email) { doc.text(agent.Email, textX, detailY); detailY += 6; }
    if (agent.Mobile) { doc.text(agent.Mobile, textX, detailY); }

    yPos = infoBoxY + infoBoxH + 14;
  } else {
    yPos += 8;
  }

  // Client name
  if (data.clientName && data.clientName.trim() !== '') {
    yPos += 10;
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Specially Prepared for', pageWidth / 2, yPos, { align: 'center' });
    yPos += 9;
    doc.setTextColor(...ORANGE_RGB);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text(data.clientName.trim(), pageWidth / 2, yPos, { align: 'center' });
    yPos += 12;
  }

  // Profiles summary
  if (data.profileNames.length > 0) {
    doc.setTextColor(200, 200, 200);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const profileText = data.profileNames.length === 1
      ? `Profile: ${data.profileNames[0]}`
      : `Profiles: ${data.profileNames.join(' & ')}`;
    doc.text(profileText, pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
  }

  // Date & disclaimer
  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;
  doc.setFillColor(...ORANGE_RGB);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 0.8, 'F');

  yPos += 10;
  doc.setTextColor(160, 160, 160);
  doc.setFontSize(7.5);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for informational purposes only. Actual loan terms may vary. Subject to MAS regulations and lender policies.';
  doc.text(doc.splitTextToSize(disclaimer, pageWidth - 50), pageWidth / 2, yPos, { align: 'center' });

  addFooter(doc, 1, logoDataUrl, logoAspect, agent);
};

// ── Timeline table page (landscape) ─────────────────────────────────────

const renderTimelinePage = (
  doc: jsPDF,
  section: PurchasePlannerPdfSection,
  profileNames: string[],
  pageNum: number,
  logoDataUrl?: string,
  logoAspect?: number,
  agentInfo?: AgentInfo,
) => {
  doc.addPage('a4', 'landscape');
  const pageWidth = doc.internal.pageSize.getWidth(); // 297mm
  const pageHeight = doc.internal.pageSize.getHeight(); // 210mm

  // Header bar
  doc.setFillColor(...SUMMARY_BAR_NAVY_RGB);
  doc.rect(0, 0, pageWidth, 10, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  const headerTitle = `PURCHASE PLANNER — ${section.propertyTypeDisplay.toUpperCase()}${section.propertyType === 'HDB' ? ` (${section.loanType === 'hdb' ? 'HDB Loan' : 'Bank Loan'})` : ''}`;
  doc.text(headerTitle, 12, 6.5);

  // Build column headers and body
  const numProfiles = section.profileRowData.length;
  const isHdbLoan = section.propertyType === 'HDB' && section.loanType === 'hdb';

  // Column group headers (top row)
  const columnGroups: Array<{ label: string; colSpan: number; fillColor: [number, number, number] }> = [];
  columnGroups.push({ label: 'TIMELINE', colSpan: 1, fillColor: [89, 89, 89] });
  for (let pi = 0; pi < numProfiles; pi++) {
    const name = profileNames[pi] || `Profile ${pi + 1}`;
    columnGroups.push({
      label: numProfiles > 1 ? name.toUpperCase() : 'CURRENT PROFILE',
      colSpan: 4,
      fillColor: pi === 0 ? [233, 115, 0] : [0, 122, 163],
    });
  }
  columnGroups.push({ label: 'FUNDS', colSpan: 3, fillColor: [76, 175, 80] });
  const propCols = isHdbLoan ? 7 : 8;
  const propColsWithAffordable = section.showAffordableToday ? propCols + 1 : propCols;
  columnGroups.push({
    label: `PROPERTY PURCHASE (${section.propertyTypeDisplay.toUpperCase()})`,
    colSpan: propColsWithAffordable,
    fillColor: [183, 110, 42],
  });

  // Column sub-headers (second row)
  const subHeaders: string[] = ['Timeline'];
  for (let pi = 0; pi < numProfiles; pi++) {
    subHeaders.push('Age', 'Assessable\nIncome', 'Cash Savings\n(Monthly)', 'CPF OA\n(Monthly)');
  }
  subHeaders.push('CPF OA', 'Cash\nSavings', 'Total\nCapital');
  if (isHdbLoan) {
    subHeaders.push('Max Purchase\nPrice', 'Max\nLoan', '25% Down\n(CPF/Cash)', 'Stamp\nDuty', 'ABSD', 'Total\nOutlay', 'Diff: Cap\nvs Outlay');
  } else {
    subHeaders.push('Max Purchase\nPrice', 'Max\nLoan', '5% Down\n(Cash)', 'Remaining\nDown (20%)', 'Stamp\nDuty', 'ABSD', 'Total\nOutlay', 'Diff: Cap\nvs Outlay');
  }
  if (section.showAffordableToday) {
    subHeaders.push('Affordable\nToday');
  }

  // Build table body rows
  const body: string[][] = section.timelineData.map((row, rowIdx) => {
    const cells: string[] = [row.date];

    // Per-profile columns
    for (let pi = 0; pi < numProfiles; pi++) {
      const prd = section.profileRowData[pi][rowIdx];
      cells.push(
        String(prd.age),
        formatCurrency(prd.assessableIncome),
        formatCurrency(prd.cashSavingsMonthly),
        formatCurrency(prd.cpfOAMonthly),
      );
    }

    // Funds columns
    cells.push(
      formatCurrency(row.cpfOAAccumulated + row.cpfRefundFromSales),
      formatCurrency(row.cashSavingsAccumulated + row.cashProceedsFromSale),
      formatCurrency(row.totalCapital),
    );

    // Property purchase columns
    const purchasePrice = row.targetPurchasePrice > 0 ? row.targetPurchasePrice : row.maxPurchasePrice;
    cells.push(formatCurrency(purchasePrice));
    cells.push(formatCurrency(row.maxLoan));

    if (isHdbLoan) {
      cells.push(formatCurrency(row.downpayment5Cash));
    } else {
      cells.push(formatCurrency(row.downpayment5Cash));
      cells.push(formatCurrency(row.remainingDownpayment));
    }

    cells.push(formatCurrency(row.stampDuty));
    cells.push(formatCurrency(row.absd));
    cells.push(formatCurrency(row.totalOutlay));
    cells.push(formatCurrency(row.difference));

    if (section.showAffordableToday) {
      cells.push(formatCurrency(row.affordableToday));
    }

    return cells;
  });

  const totalCols = subHeaders.length;
  const margin = 6;
  const tableWidth = pageWidth - margin * 2;

  // Compute column widths — give proportional space
  const profileColCount = numProfiles * 4;
  const fundsColCount = 3;
  const propColCount = propColsWithAffordable;

  // Auto column widths via autoTable
  const columnStyles: Record<number, object> = {};
  // Timeline column
  columnStyles[0] = { cellWidth: 18, halign: 'center' as const };

  // Profile columns
  let colIdx = 1;
  for (let pi = 0; pi < numProfiles; pi++) {
    columnStyles[colIdx] = { cellWidth: 10, halign: 'center' as const }; // Age
    columnStyles[colIdx + 1] = { halign: 'right' as const }; // Income
    columnStyles[colIdx + 2] = { halign: 'right' as const }; // Cash Savings
    columnStyles[colIdx + 3] = { halign: 'right' as const }; // CPF OA
    colIdx += 4;
  }

  // Funds columns
  columnStyles[colIdx] = { halign: 'right' as const };
  columnStyles[colIdx + 1] = { halign: 'right' as const };
  columnStyles[colIdx + 2] = { halign: 'right' as const, fontStyle: 'bold' as const };
  colIdx += 3;

  // Property columns
  for (let i = 0; i < propColCount; i++) {
    const isLastRegularCol = (i === propColCount - 1 && !section.showAffordableToday) || (i === propColCount - 2 && section.showAffordableToday);
    // "Diff: Cap vs Outlay" is the last col before optional "Affordable Today"
    const isDiffCol = isHdbLoan ? i === 6 : i === 7;
    columnStyles[colIdx + i] = {
      halign: 'right' as const,
      ...(isDiffCol ? { fontStyle: 'bold' as const } : {}),
    };
  }

  // Determine font size based on number of columns
  const baseFontSize = totalCols > 18 ? 5 : totalCols > 15 ? 5.5 : 6;

  // Build the group header row
  const groupHeaderRow: Array<{ content: string; colSpan: number; styles: object }> = columnGroups.map((g) => ({
    content: g.label,
    colSpan: g.colSpan,
    styles: {
      fillColor: g.fillColor,
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold' as const,
      fontSize: baseFontSize + 0.5,
      halign: 'center' as const,
      cellPadding: { top: 2, bottom: 2, left: 1.5, right: 1.5 },
    },
  }));

  // Render the table
  autoTable(doc, {
    startY: 12,
    head: [
      groupHeaderRow,
      subHeaders.map((h) => ({
        content: h,
        styles: {
          fillColor: SECTION_HEADER_BLUE_RGB,
          textColor: [255, 255, 255] as [number, number, number],
          fontStyle: 'bold' as const,
          fontSize: baseFontSize,
          halign: 'center' as const,
          cellPadding: { top: 1.5, bottom: 1.5, left: 1, right: 1 },
        },
      })),
    ],
    body,
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: baseFontSize,
      cellPadding: { top: 1.2, bottom: 1.2, left: 1.5, right: 1.5 },
      lineColor: [200, 200, 200],
      lineWidth: 0.1,
      overflow: 'linebreak',
    },
    headStyles: {
      fillColor: SECTION_HEADER_BLUE_RGB,
      textColor: [255, 255, 255] as [number, number, number],
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: ZEBRA_GRAY_RGB },
    columnStyles,
    tableWidth,
    margin: { left: margin, right: margin, bottom: 18, top: 12 },
    didParseCell: (data) => {
      // Color the "Diff" column cells
      if (data.section === 'body') {
        const diffColIdx = isHdbLoan
          ? 1 + profileColCount + fundsColCount + 5
          : 1 + profileColCount + fundsColCount + 6;

        if (data.column.index === diffColIdx) {
          const rawValue = String(data.cell.raw || '');
          // Check if the value starts with -$ (negative)
          if (rawValue.startsWith('-$') || rawValue.startsWith('-')) {
            data.cell.styles.fillColor = NEGATIVE_RED_RGB;
            data.cell.styles.textColor = [180, 0, 0];
            data.cell.styles.fontStyle = 'bold';
          } else if (rawValue.startsWith('$') && rawValue !== '$0') {
            data.cell.styles.fillColor = POSITIVE_GREEN_RGB;
            data.cell.styles.textColor = [0, 100, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        }
      }
    },
  });

  addFooter(doc, pageNum, logoDataUrl, logoAspect, agentInfo);
};

// ── Main export function ────────────────────────────────────────────────

export const exportPurchasePlannerToPDF = async (
  data: PurchasePlannerPdfData,
  filename: string = 'purchase-planner-report.pdf',
): Promise<void> => {
  // Start with portrait for cover page
  // compress: the Huttons logo is a 4210x1035 PNG that jsPDF embeds as raw RGBA —
  // 17 MB of a 17 MB report. Flate-compressing the streams brings the same file to ~50 KB.
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });

  // Pre-load Huttons logo
  let logoDataUrl: string | undefined;
  let logoAspect: number | undefined;
  try {
    const logoImg = await loadImageEl(HuttonsWhiteLogo);
    logoDataUrl = HuttonsWhiteLogo;
    logoAspect = logoImg.width / logoImg.height;
  } catch { /* fallback to text */ }

  // Page 1: Cover (portrait)
  await renderCoverPage(doc, data, logoDataUrl, logoAspect);

  // Pages 2+: Timeline tables (landscape, one per section)
  for (let i = 0; i < data.sections.length; i++) {
    renderTimelinePage(
      doc,
      data.sections[i],
      data.profileNames,
      i + 2,
      logoDataUrl,
      logoAspect,
      data.agentInfo,
    );
  }

  await savePdfDocument(doc, filename);
};
